/**
 * Quantity Discount Progress Bar Component
 * Self-updating progress bar that reacts to cart changes automatically
 */

class QuantityDiscountProgressBar extends HTMLElement {
  constructor() {
    super();
    this.tier1Qty = parseInt(this.dataset.tier1Qty) || 2;
    this.tier1Percent = parseInt(this.dataset.tier1Percent) || 10;
    this.tier2Qty = parseInt(this.dataset.tier2Qty) || 3;
    this.tier2Percent = parseInt(this.dataset.tier2Percent) || 15;
    this.tier3Qty = parseInt(this.dataset.tier3Qty) || 4;
    this.tier3Percent = parseInt(this.dataset.tier3Percent) || 20;
  }

  connectedCallback() {
    // Initialize with current item count from data attribute
    const itemCount = parseInt(this.dataset.itemCount) || 0;
    this.init(itemCount);
  }

  init(itemCount) {
    // Mark that we've been programmatically initialized with this count
    // to prevent MutationObserver from overwriting with stale data
    this.setAttribute('data-item-count', itemCount);
    this._lastInitTime = Date.now();

    this.updateProgress(itemCount);
    this.updateMessage(itemCount);
    this.updateMilestones(itemCount);
    this.updateLabels(itemCount);
  }

  updateProgress(itemCount) {
    const progressBar = this.querySelector('.qty-discount-progress');
    if (!progressBar) return;

    let progressPercent = 0;

    if (itemCount >= this.tier3Qty) {
      progressPercent = 100;
    } else if (itemCount >= this.tier2Qty) {
      progressPercent = 66.66;
    } else if (itemCount >= this.tier1Qty) {
      progressPercent = 33.33;
    } else {
      progressPercent = (itemCount / this.tier1Qty) * 33.33;
    }

    progressBar.style.width = `${progressPercent}%`;
  }

  updateMessage(itemCount) {
    const messageEl = this.querySelector('.qty-discount-message');
    if (!messageEl) return;

    let message = '';
    const icon = itemCount >= this.tier3Qty ? '🎉' : '🛒';

    if (itemCount >= this.tier3Qty) {
      message = `<span class="qty-discount-icon">${icon}</span><span>Congratulations! You've got <strong>${this.tier3Percent}% discount!</strong></span>`;
    } else if (itemCount >= this.tier2Qty) {
      const itemsNeeded = this.tier3Qty - itemCount;
      const plural = itemsNeeded > 1 ? 's' : '';
      message = `<span class="qty-discount-icon">${icon}</span><span>Add <strong>${itemsNeeded}</strong> more item${plural} to get <strong>${this.tier3Percent}% discount</strong></span>`;
    } else if (itemCount >= this.tier1Qty) {
      const itemsNeeded = this.tier2Qty - itemCount;
      const plural = itemsNeeded > 1 ? 's' : '';
      message = `<span class="qty-discount-icon">${icon}</span><span>Add <strong>${itemsNeeded}</strong> more item${plural} to get <strong>${this.tier2Percent}% discount</strong></span>`;
    } else {
      const itemsNeeded = this.tier1Qty - itemCount;
      const plural = itemsNeeded > 1 ? 's' : '';
      message = `<span class="qty-discount-icon">${icon}</span><span>Add <strong>${itemsNeeded}</strong> more item${plural} to unlock <strong>${this.tier1Percent}% discount</strong></span>`;
    }

    messageEl.innerHTML = message;
  }

  updateMilestones(itemCount) {
    const dots = this.querySelectorAll('.qty-milestone-dot');
    if (!dots.length) return;

    dots.forEach((dot, index) => {
      const tierQty = index === 0 ? this.tier1Qty : index === 1 ? this.tier2Qty : this.tier3Qty;

      if (itemCount >= tierQty) {
        if (!dot.classList.contains('achieved')) {
          dot.classList.add('achieved');
        }
      } else {
        dot.classList.remove('achieved');
      }
    });
  }

  updateLabels(itemCount) {
    const labels = this.querySelectorAll('.qty-label');
    if (!labels.length) return;

    labels.forEach((label, index) => {
      const tierQty = index === 0 ? this.tier1Qty : index === 1 ? this.tier2Qty : this.tier3Qty;

      if (itemCount >= tierQty) {
        label.classList.add('achieved');
      } else {
        label.classList.remove('achieved');
      }
    });
  }
}

customElements.define('quantity-discount-progress-bar', QuantityDiscountProgressBar);

// Global function to update quantity discount progress bar with correct item count
// Call this after AJAX cart updates with the new item count
window.updateQtyDiscountProgressBar = function (itemCount) {
  // Use setTimeout to ensure this runs after DOM mutations are complete
  setTimeout(() => {
    const bars = document.querySelectorAll('quantity-discount-progress-bar');
    bars.forEach(bar => {
      bar.setAttribute('data-item-count', itemCount);
      bar.init(itemCount);
    });
  }, 10);
};

// Auto-initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('quantity-discount-progress-bar').forEach(bar => {
    const itemCount = parseInt(bar.dataset.itemCount) || 0;
    bar.init(itemCount);
  });

  // Auto-update progress bar when mini-cart content changes
  // This catches all cart add/update actions without modifying product.min.js
  const miniCartObserver = new MutationObserver((mutations) => {
    // Debounce: only act if significant DOM changes occurred
    const hasSignificantChanges = mutations.some(mutation =>
      mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0
    );

    if (hasSignificantChanges) {
      // Fetch latest cart data and update progress bar
      fetch('/cart.json')
        .then(resp => resp.json())
        .then(cart => {
          if (typeof cart.item_count !== 'undefined') {
            window.updateQtyDiscountProgressBar(cart.item_count);
          }
        })
        .catch(() => { }); // Silently ignore errors
    }
  });

  // Observe the mini-cart container for changes
  const miniCartContainer = document.getElementById('form-mini-cart');
  if (miniCartContainer) {
    miniCartObserver.observe(miniCartContainer, {
      childList: true,
      subtree: true
    });
  }

  // Also listen for when the mini-cart is opened
  const miniCartWrapper = document.getElementById('bls-header_minicart');
  if (miniCartWrapper) {
    const classObserver = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.attributeName === 'class') {
          const target = mutation.target;
          if (target.classList.contains('bls__opend-popup-header')) {
            // Mini-cart was just opened, fetch latest cart data
            fetch('/cart.json')
              .then(resp => resp.json())
              .then(cart => {
                if (typeof cart.item_count !== 'undefined') {
                  window.updateQtyDiscountProgressBar(cart.item_count);
                }
              })
              .catch(() => { });
          }
        }
      });
    });

    classObserver.observe(miniCartWrapper, { attributes: true });
  }
});

